import type { Locale } from '~/build/i18n'

interface ProjectConfig {
  /** 产品名称 */
  projectName: string

  /** projectChannel 后台配置用于index接口区分项目 */
  projectChannel: string
  /**
   * 'ldShop'：shop图标
   * 'mildom'：日本定制图标
   * 'topUp'：投放图标
   * 'tideGame'：盛天图标
   */
  logoType: 'ldShop' | 'mildom' | 'topUp' | 'tideGame'

  /** 默认支付货币 默认空使用接口的select选中的 */
  defaultCurrency: string
  /** 过滤货币列表，默认为空，不过滤 */
  filterCurrencyList: string[]
  /** 展示头部语言切换入口 */
  isNavLanguageShown: boolean
  /** 展示下载按钮 */
  isDownloadShown: boolean

  /** 尾部facebook链接 */
  footerFacebookUrl: string
  /** 尾部facebook繁中语言的链接 */
  footerFacebookUrlForTw?: string
  /** 尾部line链接 */
  footerLineUrl: string
  /** 尾部discord链接 */
  footerDiscordUrl: string
  /** 允许SEO */
  hasSeo: boolean
  domain: string
  hasKOL: boolean
}

interface BuildProjectConfig {
  /** 默认语言 */
  defaultLocale: string
  /** 语言包，空数组是所有 */
  locales: Locale[]
  /** 域名 */
  domain: string

}

/**
 * production: ldshop
 * production_ad: 投放
 * production_jp: 日本定制
 * production_tide_game: 盛天定制
 */
export type ProjectType = 'production' | 'production_ad' | 'production_jp' | 'production_tide_game' | string

/** 打包时配置 */
function getBuildConfig(type: ProjectType) {
  /** shop的配置 */
  const shopConfig: BuildProjectConfig = {
    defaultLocale: 'en',
    locales: [],
    domain: 'https://www.ldshop.gg',
  }

  /** shop广告 */
  const shopAdConfig: BuildProjectConfig = {
    defaultLocale: 'en',
    locales: [],
    domain: 'https://www.gamingvirtual.shop',
  }

  /** 日本定制 */
  const mildomConfig: BuildProjectConfig = {
    defaultLocale: 'jp',
    locales: ['jp'],
    domain: 'https://mildom.ldshop.gg',
  }

  /** 盛天定制 */
  const tideGameConfig: BuildProjectConfig = {
    defaultLocale: 'tw',
    locales: ['tw'],
    domain: 'https://t1degames.ldshop.gg',
  }

  const configMap: Record<ProjectType, BuildProjectConfig> = {
    production: shopConfig,
    production_ad: shopAdConfig,
    production_jp: mildomConfig,
    production_tide_game: tideGameConfig,
  }

  return configMap[type] || shopConfig
}

/** 运行时配置 */
function getRuntimeConfig(type: ProjectType) {
  /** 测试开发 环境 */
  const isTestOrisDev = isTest()

  const { queryData } = storeToRefs(useInitQuery())

  /** shop的配置 */
  const shopConfig: ProjectConfig = {
    projectName: 'LDShop',
    // 在主站上，URL带有Channel的，优先使用。否则用主站的的Channel
    projectChannel: queryData.value.platformChannel || (isTestOrisDev ? 'ldshop-test' : 'ldshop'),
    logoType: 'ldShop',
    defaultCurrency: '',
    filterCurrencyList: [],
    isNavLanguageShown: true,
    isDownloadShown: true,
    footerFacebookUrlForTw: 'https://www.facebook.com/LDSPACEtc',
    footerFacebookUrl: 'https://www.facebook.com/LDShop.gg/',
    footerLineUrl: 'https://line.me/ti/p/rU-EAXtm4N',
    footerDiscordUrl: 'https://discord.gg/WdhgDsvhrS',
    hasSeo: true,
    hasKOL: true,
    domain: 'https://www.ldshop.gg',
  }

  /** shop广告投放 */
  const shopAdConfig: ProjectConfig = {
    projectName: 'Top up',
    projectChannel: queryData.value.platformChannel || (isTestOrisDev ? 'ldshop-ad-test' : 'ldshop-ad'),
    logoType: 'topUp',
    defaultCurrency: '',
    filterCurrencyList: [],
    isNavLanguageShown: true,
    isDownloadShown: false,
    footerFacebookUrl: 'https://www.facebook.com/topup.shop.game',
    footerLineUrl: 'https://line.me/ti/p/rU-EAXtm4N',
    footerDiscordUrl: '',
    hasSeo: false,
    hasKOL: false,
    domain: 'https://www.gamingvirtual.shop',
  }

  /** 日本定制 */
  const mildomConfig: ProjectConfig = {
    projectName: 'Mildom x LDShop',
    projectChannel: isTestOrisDev ? 'ldshop-jp-test' : 'ldshop-jp',
    logoType: 'mildom',
    defaultCurrency: 'JPY',
    filterCurrencyList: [],
    isNavLanguageShown: false,
    isDownloadShown: false,
    footerFacebookUrl: 'https://www.facebook.com/LDSPACEtc',
    footerLineUrl: 'https://line.me/ti/p/rU-EAXtm4N',
    footerDiscordUrl: '',
    hasSeo: false,
    hasKOL: false,
    domain: 'https://mildom.ldshop.gg',
  }

  /** 盛天定制 */
  const tideGameConfig: ProjectConfig = {
    projectName: 't1degames x LDShop',
    projectChannel: isTestOrisDev ? 'ldshop-tide-games-test' : 'ldshop-tide-games',
    logoType: 'tideGame',
    defaultCurrency: 'TWD',
    filterCurrencyList: ['TWD', 'HKD'],
    isNavLanguageShown: true,
    isDownloadShown: false,
    footerFacebookUrl: 'https://www.facebook.com/LDSPACEtc',
    footerLineUrl: 'https://line.me/ti/p/rU-EAXtm4N',
    footerDiscordUrl: '',
    hasSeo: false,
    hasKOL: false,
    domain: 'https://t1degames.ldshop.gg',
  }

  const configMap: Record<ProjectType, ProjectConfig> = {
    production: shopConfig,
    production_ad: shopAdConfig,
    production_jp: mildomConfig,
    production_tide_game: tideGameConfig,
  }

  return configMap[type] || shopConfig
}

/** 打包时执行 */
export function useProjectBuildConfig() {
  const env = process.env.NUXT_PUBLIC_ENV as ProjectType

  return getBuildConfig(env)
}

/** 运行时执行 */
export function useProjectRuntimeConfig() {
  const { public: { env } } = useRuntimeConfig()

  /**
   * 本地环境、测试环境、预发布环境不分配node集群，通过URL的projectType参数来区分配置
   */
  if (isTest()) {
    const { queryData } = storeToRefs(useInitQuery())
    const projectType = queryData.value?.projectType

    // 在非正式环境处理语言问题，url上还会有/jp, 正式环境不会有。
    const { $i18n: { setLocale, defaultLocale } } = useNuxtApp()
    const { defaultLocale: buildLocale } = getBuildConfig(projectType)
    if (defaultLocale !== buildLocale) {
      setLocale(buildLocale)
    }

    return getRuntimeConfig(projectType)
  }

  return getRuntimeConfig(env)
}
